import { Download, PlayArrow } from '@mui/icons-material';
import { Button, ButtonGroup } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { Constants } from '../../../../../../Core/Constants';
import { CustomerReportModel, GetDailyPositionsReportRequest, PositionModel } from '../../../../../../Generated/Commodity-Management-Api';
import { downloadFile } from '../../../../../../Helpers';
import { deepMerge } from '../../../../../../Helpers/ObjectHelper';
import { useLoadingState } from '../../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../../Redux/ReduxStore';
import { StoneXGrid, StoneXRow } from '../../../../../StoneX';
import { StoneXButton, StoneXDatePicker } from '../../../../../StoneXMui';
import { ReportProps } from '../../ReportsPage';
import DailyPositionsReportSettingsModal from './DailyPositionsReportSettingsModal';
import {
  DAILY_POSITIONS_ALL_COLUMNS,
  DailyPositionsColumn,
  DailyPositionsColumns,
  DailyPositionsReportConfiguration,
  DailyPositionsReportRow,
  DailyPositionsReportUserOverride,
  calculateTotalVolume,
} from './Types';

function getActiveColumns(configuration: DailyPositionsReportConfiguration | undefined): DailyPositionsColumn[] {
  const active = configuration?.columns?.active;
  const available = configuration?.columns?.available ?? DAILY_POSITIONS_ALL_COLUMNS;

  const source = active && active.length ? active : available;
  return source.filter((column): column is DailyPositionsColumn => DAILY_POSITIONS_ALL_COLUMNS.includes(column as DailyPositionsColumn));
}

export function DailyPositionsReport(props: ReportProps) {
  const { customerReportId } = props;

  const customerReport = useApplicationSelector((state: RootState): CustomerReportModel =>
    state.commodityManagement.customerReports.find((x: any) => x.customerReportId === customerReportId)!);

  const reportConfiguration = useMemo(
    () => (customerReport.configuration ? JSON.parse(customerReport.configuration) as DailyPositionsReportConfiguration : {} as DailyPositionsReportConfiguration),
    [customerReport],
  );
  const userOverrides = useMemo(
    () => (customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as DailyPositionsReportUserOverride : undefined),
    [customerReport],
  );
  const reportSettings = useMemo(
    () => deepMerge(reportConfiguration, userOverrides) as DailyPositionsReportConfiguration,
    [reportConfiguration, userOverrides],
  );

  const activeColumns = useMemo(() => getActiveColumns(reportSettings), [reportSettings]);
  const groupByCommodity = reportSettings?.behavior?.groupByCommodity ?? false;

  const [startDate, setStartDate] = useState<Date | null>(props.quoteDate);
  const [endDate, setEndDate] = useState<Date | null>(props.quoteDate);
  const [rows, setRows] = useState<PositionModel[]>([]);

  const reportLoadingState = useLoadingState();
  const reportDownloadingState = useLoadingState();

  const [isConfiguringReport, setIsConfiguringReport] = useState<boolean>(false);

  function buildRequest(): GetDailyPositionsReportRequest | null {
    if (!startDate || !endDate) {
      return null;
    }

    return {
      customerReportId,
      pricingStartDate: dayjs(startDate).format('YYYY-MM-DD'),
      pricingEndDate: dayjs(endDate).format('YYYY-MM-DD'),
    };
  }

  function runReport() {
    const request = buildRequest();
    if (!request) {
      return;
    }

    CommodityManagementApi.getDailyPositionsReport(reportLoadingState.setLoadingState, request.pricingStartDate, request.pricingEndDate, request).then((response) => {
      setRows(response.data.rows ?? []);
    });
  }

  function downloadToExcel() {
    const request = buildRequest();
    if (!request) {
      return;
    }

    CommodityManagementApi.getDailyPositionsExcelReport(
      reportDownloadingState.setLoadingState,
      request.pricingStartDate,
      request.pricingEndDate,
      request,
      { responseType: 'blob', headers: { 'ContentType': Constants.MediaType.Excel } },
    ).then(downloadFile);
  }

  const gridRows = useMemo<DailyPositionsReportRow[]>(() => rows.map((row, rowIndex) => {
    const totalVolume = calculateTotalVolume(row);
    const price = row.price ?? 0;
    const rowKey = row.positionId ? String(row.positionId) : `row-${rowIndex}`;
    const commodityName = row.commodity?.commodityName || '(no commodity)';
    return {
      ...row,
      rowKey,
      path: groupByCommodity ? [commodityName, rowKey] : [rowKey],
      totalVolume,
      totalValue: totalVolume * price,
    };
  }), [rows, groupByCommodity]);

  const gridColumnDefinitions = useMemo<ColDef<DailyPositionsReportRow>[]>(
    () => activeColumns.map((column) => DailyPositionsColumns[column] as ColDef<DailyPositionsReportRow>),
    [activeColumns],
  );

  const gridOptions = useMemo<AgGridReactProps<DailyPositionsReportRow>>(() => ({
    grandTotalRow: 'bottom',
    groupTotalRow: groupByCommodity ? 'bottom' : undefined,
    groupDefaultExpanded: -1,
  }), [groupByCommodity]);

  return (
    <div>
      <StoneXRow>
        <StoneXDatePicker label="Start Date" value={startDate} onChange={setStartDate} />
        <StoneXDatePicker label="End Date" value={endDate} onChange={setEndDate} />
        <ButtonGroup>
          <StoneXButton onClick={runReport} isWorking={reportLoadingState.isLoading()} startIcon={<PlayArrow />}>Run</StoneXButton>
          <StoneXButton variant="outlined" onClick={downloadToExcel} isWorking={reportDownloadingState.isLoading()} startIcon={<Download />}>Excel</StoneXButton>
        </ButtonGroup>
        <Button className='pull-right' variant='outlined' onClick={() => setIsConfiguringReport(true)}>Configure Report</Button>
      </StoneXRow>

      {reportLoadingState.isLoaded() && gridRows.length === 0 && (
        <p>No daily positions found between {dayjs(startDate).format('YYYY-MM-DD')} and {dayjs(endDate).format('YYYY-MM-DD')}.</p>
      )}

      {reportLoadingState.isLoaded() && gridRows.length > 0 && <StoneXGrid
        rows={gridRows}
        columns={gridColumnDefinitions}
        hideGridOptions
        onlyShowGroupLabels={groupByCommodity}
        getRowId={(params) => params.data.rowKey}
        gridOptions={gridOptions}
      />}

      <DailyPositionsReportSettingsModal
        open={isConfiguringReport}
        customerReport={customerReport}
        onClose={() => setIsConfiguringReport(false)}
        onSave={() => setIsConfiguringReport(false)}
      />
    </div>
  );
}
